import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, TextField, MenuItem, Button, Box, Typography, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const MILESTONE_OPTIONS = [
  { value: 'pedido', label: 'Pedido' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'factura', label: 'Factura' },
  { value: 'puesta_en_marcha', label: 'Puesta en marcha' },
  { value: 'recepcion_provisional', label: 'Recepción provisional' },
  { value: 'montaje', label: 'Montaje' },
];

const DELAY_FROM_OPTIONS = [
  { value: 'milestone', label: 'Hito' },
  { value: 'invoice_date', label: 'Fecha factura' },
  { value: 'delivery_date', label: 'Fecha entrega' },
];

const emptyCondition = {
  description: '',
  percentage: '',
  milestone: 'pedido',
  delay_days: 0,
  delay_from: 'milestone',
};

/**
 * PaymentConditionsEditor - Editable table for structured payment conditions.
 *
 * Props:
 *   conditions: array of condition objects
 *   onAdd: (data) => Promise  — create a new condition
 *   onUpdate: (id, data) => Promise  — update existing condition
 *   onDelete: (id) => Promise  — delete a condition
 *   onCopyDefaults: () => Promise  — optional: copy from supplier defaults
 *   readOnly: boolean
 *   amount: number — optional total amount to compute column
 */
export default function PaymentConditionsEditor({
  conditions = [],
  onAdd,
  onUpdate,
  onDelete,
  onCopyDefaults,
  readOnly = false,
  amount = 0,
}) {
  const [newCond, setNewCond] = useState({ ...emptyCondition });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleAdd = async () => {
    if (!newCond.description || !newCond.percentage) return;
    await onAdd({
      ...newCond,
      percentage: parseFloat(newCond.percentage) || 0,
      delay_days: parseInt(newCond.delay_days) || 0,
    });
    setNewCond({ ...emptyCondition });
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditData({
      description: c.description,
      percentage: c.percentage,
      milestone: c.milestone,
      delay_days: c.delay_days || 0,
      delay_from: c.delay_from || 'milestone',
    });
  };

  const handleSave = async (id) => {
    await onUpdate(id, {
      ...editData,
      percentage: parseFloat(editData.percentage) || 0,
      delay_days: parseInt(editData.delay_days) || 0,
    });
    setEditingId(null);
  };

  const totalPct = conditions.reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0);

  const fmtEUR = (v) => {
    const n = parseFloat(v) || 0;
    return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Box>
      {!readOnly && onCopyDefaults && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Copiar condiciones por defecto del proveedor">
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={onCopyDefaults}>
              Copiar del proveedor
            </Button>
          </Tooltip>
        </Box>
      )}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#c62828' }}>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Descripcion</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">%</TableCell>
            {amount > 0 && <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Importe</TableCell>}
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Hito</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Dias</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Desde</TableCell>
            {!readOnly && <TableCell align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {conditions.map((c) => (
            <TableRow key={c.id} hover>
              {editingId === c.id ? (
                <>
                  <TableCell>
                    <TextField size="small" fullWidth value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })} />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>
                    <TextField size="small" type="number" value={editData.percentage}
                      onChange={(e) => setEditData({ ...editData, percentage: e.target.value })}
                      sx={{ width: 70 }} />
                  </TableCell>
                  {amount > 0 && (
                    <TableCell align="right">{fmtEUR((parseFloat(editData.percentage) || 0) * amount / 100)}</TableCell>
                  )}
                  <TableCell>
                    <TextField size="small" select value={editData.milestone}
                      onChange={(e) => setEditData({ ...editData, milestone: e.target.value })}
                      sx={{ minWidth: 120 }}>
                      {MILESTONE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>
                    <TextField size="small" type="number" value={editData.delay_days}
                      onChange={(e) => setEditData({ ...editData, delay_days: e.target.value })}
                      sx={{ width: 70 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" select value={editData.delay_from}
                      onChange={(e) => setEditData({ ...editData, delay_from: e.target.value })}
                      sx={{ minWidth: 110 }}>
                      {DELAY_FROM_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleSave(c.id)}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell
                    onClick={() => !readOnly && startEdit(c)}
                    sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                  >
                    {c.description}
                  </TableCell>
                  <TableCell align="right"
                    onClick={() => !readOnly && startEdit(c)}
                    sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                  >
                    {c.percentage}%
                  </TableCell>
                  {amount > 0 && (
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {fmtEUR((parseFloat(c.percentage) || 0) * amount / 100)}
                    </TableCell>
                  )}
                  <TableCell
                    onClick={() => !readOnly && startEdit(c)}
                    sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                  >
                    {MILESTONE_OPTIONS.find((o) => o.value === c.milestone)?.label || c.milestone}
                  </TableCell>
                  <TableCell align="right"
                    onClick={() => !readOnly && startEdit(c)}
                    sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                  >
                    {c.delay_days || 0}
                  </TableCell>
                  <TableCell
                    onClick={() => !readOnly && startEdit(c)}
                    sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                  >
                    {DELAY_FROM_OPTIONS.find((o) => o.value === c.delay_from)?.label || c.delay_from || 'Hito'}
                  </TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          ))}

          {/* Add new row */}
          {!readOnly && (
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Descripcion" value={newCond.description}
                  onChange={(e) => setNewCond({ ...newCond, description: e.target.value })} />
              </TableCell>
              <TableCell align="right" sx={{ width: 80 }}>
                <TextField size="small" type="number" placeholder="%" value={newCond.percentage}
                  onChange={(e) => setNewCond({ ...newCond, percentage: e.target.value })}
                  sx={{ width: 70 }} />
              </TableCell>
              {amount > 0 && <TableCell />}
              <TableCell>
                <TextField size="small" select value={newCond.milestone}
                  onChange={(e) => setNewCond({ ...newCond, milestone: e.target.value })}
                  sx={{ minWidth: 120 }}>
                  {MILESTONE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell align="right" sx={{ width: 80 }}>
                <TextField size="small" type="number" value={newCond.delay_days}
                  onChange={(e) => setNewCond({ ...newCond, delay_days: e.target.value })}
                  sx={{ width: 70 }} />
              </TableCell>
              <TableCell>
                <TextField size="small" select value={newCond.delay_from}
                  onChange={(e) => setNewCond({ ...newCond, delay_from: e.target.value })}
                  sx={{ minWidth: 110 }}>
                  {DELAY_FROM_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" color="primary" onClick={handleAdd}
                  disabled={!newCond.description || !newCond.percentage}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {conditions.length > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', gap: 2 }}>
          <Typography variant="caption" color={Math.abs(totalPct - 100) < 0.01 ? 'success.main' : 'warning.main'}>
            Total: {totalPct.toFixed(1)}%
          </Typography>
          {amount > 0 && (
            <Typography variant="caption">
              Importe total: {fmtEUR(amount)} EUR
            </Typography>
          )}
        </Box>
      )}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar esta condicion de pago?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={async () => { await onDelete(deleteConfirmId); setDeleteConfirmId(null); }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
